import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, imgUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

export default function Checkout() {
  const { t } = useLang();
  const { items, subtotal, shipping } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", address: "", city: "", zip: "", country: "Italia",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
        customer: form,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="py-32 text-center" data-testid="checkout-empty">
        <p className="text-cocoa/60">{t("cart.empty")}</p>
        <Link to="/shop" className="mt-4 inline-block text-honey font-medium underline underline-offset-4">{t("cart.empty.cta")}</Link>
      </div>
    );
  }

  const inputCls = "w-full bg-white border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="checkout-page">
      <h1 className="font-serif text-4xl font-bold tracking-tight" data-testid="checkout-title">{t("checkout.title")}</h1>
      {!user && (
        <p className="mt-3 text-sm text-cocoa/60">
          <Link to="/accedi?redirect=/checkout" className="text-honey underline underline-offset-4" data-testid="checkout-login-link">{t("checkout.login.hint")}</Link>
        </p>
      )}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          onSubmit={submit} className="lg:col-span-3 space-y-4" data-testid="checkout-form">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-800">{t("checkout.contact")}</p>
          <input required value={form.name} onChange={set("name")} placeholder={t("checkout.name")} data-testid="checkout-name" className={inputCls} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required type="email" value={form.email} onChange={set("email")} placeholder={t("checkout.email")} data-testid="checkout-email" className={inputCls} />
            <input value={form.phone} onChange={set("phone")} placeholder={t("checkout.phone")} data-testid="checkout-phone" className={inputCls} />
          </div>
          <input required value={form.address} onChange={set("address")} placeholder={t("checkout.address")} data-testid="checkout-address" className={inputCls} />
          <div className="grid grid-cols-2 gap-4">
            <input required value={form.city} onChange={set("city")} placeholder={t("checkout.city")} data-testid="checkout-city" className={inputCls} />
            <input required value={form.zip} onChange={set("zip")} placeholder={t("checkout.zip")} data-testid="checkout-zip" className={inputCls} />
          </div>
          <button type="submit" disabled={loading} data-testid="checkout-pay-btn"
            className="w-full flex items-center justify-center gap-2 bg-honey text-bark py-4 font-semibold hover:bg-gold transition-colors duration-300 disabled:opacity-60 mt-2">
            <CreditCard size={17} /> {loading ? t("common.loading") : `${t("checkout.pay")} — €${(subtotal + shipping).toFixed(2)}`}
          </button>
          <p className="flex items-center gap-2 text-xs text-cocoa/50 justify-center"><ShieldCheck size={14} className="text-sage" /> {t("checkout.secure")}</p>
        </motion.form>
        <motion.aside initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-2 bg-cream/60 border border-[#E5D9C8] p-7 h-fit" data-testid="checkout-summary">
          <p className="font-serif text-xl font-semibold mb-5">{t("cart.title")}</p>
          <div className="space-y-4">
            {items.map((i) => (
              <div key={i.key} className="flex gap-3 items-center">
                <img src={imgUrl(i.image)} alt="" className="w-12 h-12 object-cover rounded-sm border border-[#E5D9C8]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{i.name_it}</p>
                  <p className="text-xs font-mono text-cocoa/50">{i.size} × {i.qty}</p>
                </div>
                <p className="font-mono text-sm">€{(i.price * i.qty).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-[#E5D9C8] space-y-2 text-sm">
            <div className="flex justify-between text-cocoa/70"><span>{t("cart.subtotal")}</span><span className="font-mono" data-testid="summary-subtotal">€{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-cocoa/70"><span>{t("cart.shipping")}</span><span className="font-mono" data-testid="summary-shipping">{shipping === 0 ? t("cart.free") : `€${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-serif text-xl font-semibold pt-2 border-t border-[#E5D9C8]"><span>{t("cart.total")}</span><span className="font-mono text-honey" data-testid="summary-total">€{(subtotal + shipping).toFixed(2)}</span></div>
          </div>
        </motion.aside>
      </div>
    </div>
  );
}
